import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { UserRole } from '@/lib/supabase';

type Mode = 'login' | 'register';
type LoginMethod = 'email' | 'phone';

const roleOptions: { value: UserRole; label: string; icon: string; desc: string }[] = [
  { value: 'student', label: '学生', icon: 'ri-graduation-cap-line', desc: '备考高考/考研/各类考试' },
  { value: 'teacher', label: '教师', icon: 'ri-user-star-line', desc: '发布课程，在线答疑' },
  { value: 'parent', label: '家长', icon: 'ri-parent-line', desc: '帮孩子找学伴，监督学习' },
  { value: 'merchant', label: '商家', icon: 'ri-store-2-line', desc: '销售学习用品和儿童服装' },
];

const gradeOptions = [
  '小学一年级','小学二年级','小学三年级','小学四年级','小学五年级','小学六年级',
  '初中一年级','初中二年级','初中三年级',
  '高中一年级','高中二年级','高中三年级',
  '大学','考研备考',
];

// OTP input component
function OtpInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.padEnd(6, '').split('').slice(0, 6);

  const handleChange = (i: number, v: string) => {
    const d = v.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = d;
    onChange(next.join(''));
    if (d && i < 5) inputs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted) { onChange(pasted); e.preventDefault(); }
  };

  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { inputs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i] || ''}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          className="w-11 h-12 text-center text-lg font-bold border-2 rounded-xl outline-none transition-all border-gray-200 focus:border-red-500 bg-gray-50"
        />
      ))}
    </div>
  );
}

export default function AuthPage() {
  const [mode, setMode] = useState<Mode>('login');
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('email');
  const [role, setRole] = useState<UserRole>('student');
  const [step, setStep] = useState<1 | 2 | 'otp'>(1);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '', confirm: '', grade: '', region: '', subject: '',
  });
  const [otp, setOtp] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const navigate = useNavigate();
  const { signIn, signUp, signInWithPhone, verifyPhoneOtp } = useAuth();

  // Countdown timer for OTP resend
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const resetState = () => {
    setStep(1); setError(''); setOtp(''); setCountdown(0);
    setForm({ name: '', email: '', phone: '', password: '', confirm: '', grade: '', region: '', subject: '' });
  };

  // ── EMAIL LOGIN ──
  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.email || !form.password) { setError('请填写邮箱和密码'); return; }
    setLoading(true);
    const { error: err } = await signIn(form.email, form.password);
    setLoading(false);
    if (err) {
      if (err.includes('Invalid login credentials')) setError('邮箱或密码错误，请重试');
      else if (err.includes('Email not confirmed')) setError('请先验证邮箱后再登录');
      else setError(err);
      return;
    }
    navigate('/profile');
  };

  // ── PHONE LOGIN: send OTP ──
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const phone = form.phone.trim();
    if (!phone) { setError('请输入手机号'); return; }
    if (!/^\+?[\d\s\-]{8,15}$/.test(phone)) { setError('请输入有效的手机号（建议加国际区号，如 +86138...）'); return; }
    setLoading(true);
    const { error: err } = await signInWithPhone(phone.startsWith('+') ? phone : `+86${phone}`);
    setLoading(false);
    if (err) { setError(err); return; }
    setStep('otp');
    setCountdown(60);
  };

  // ── PHONE LOGIN: verify OTP ──
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (otp.length < 6) { setError('请输入完整的6位验证码'); return; }
    setLoading(true);
    const phone = form.phone.startsWith('+') ? form.phone : `+86${form.phone}`;
    const { error: err } = await verifyPhoneOtp(phone, otp);
    setLoading(false);
    if (err) { setError('验证码错误或已过期，请重试'); return; }
    navigate('/profile');
  };

  // ── EMAIL REGISTER step 1 ──
  const handleRegisterStep1 = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.name) { setError('请填写昵称'); return; }
    if (loginMethod === 'email') {
      if (!form.email || !form.password) { setError('请填写邮箱和密码'); return; }
      if (form.password !== form.confirm) { setError('两次密码不一致'); return; }
      if (form.password.length < 6) { setError('密码至少6位'); return; }
    } else {
      if (!form.phone) { setError('请填写手机号'); return; }
    }
    setStep(2);
  };

  // ── EMAIL REGISTER step 2 ──
  const handleEmailRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const profileData = {
      name: form.name, role, phone: form.phone,
      grade: role === 'teacher' ? form.subject : form.grade,
      region: form.region,
    };
    const { error: err } = await signUp(form.email, form.password, profileData);
    setLoading(false);
    if (err) {
      if (err.includes('already registered') || err.includes('already been registered')) setError('该邮箱已注册，请直接登录');
      else setError(err);
      return;
    }
    setSuccessMsg('注册成功！请查收验证邮件，验证后即可登录。');
  };

  // ── PHONE REGISTER: send OTP ──
  const handlePhoneRegisterSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const phone = form.phone.trim();
    if (!phone) { setError('请输入手机号'); return; }
    setLoading(true);
    const { error: err } = await signInWithPhone(phone.startsWith('+') ? phone : `+86${phone}`);
    setLoading(false);
    if (err) { setError(err); return; }
    setStep('otp');
    setCountdown(60);
  };

  // ── PHONE REGISTER: verify OTP ──
  const handlePhoneRegisterVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (otp.length < 6) { setError('请输入完整的6位验证码'); return; }
    setLoading(true);
    const phone = form.phone.startsWith('+') ? form.phone : `+86${form.phone}`;
    const profileData = {
      name: form.name, role,
      grade: role === 'teacher' ? form.subject : form.grade,
      region: form.region,
    };
    const { error: err } = await verifyPhoneOtp(phone, otp, profileData);
    setLoading(false);
    if (err) { setError('验证码错误或已过期，请重试'); return; }
    navigate('/profile');
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;
    setError('');
    const phone = form.phone.startsWith('+') ? form.phone : `+86${form.phone}`;
    setLoading(true);
    await signInWithPhone(phone);
    setLoading(false);
    setCountdown(60);
    setOtp('');
  };

  // ── SUCCESS SCREEN ──
  if (successMsg) {
    return (
      <div className="min-h-screen bg-[#faf9f7] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
            <i className="ri-mail-check-line text-4xl text-green-600"></i>
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-3">注册成功！</h2>
          <p className="text-gray-500 mb-2">{successMsg}</p>
          <p className="text-gray-400 text-sm mb-8">如未收到邮件，请检查垃圾邮件文件夹</p>
          <button
            onClick={() => { setSuccessMsg(''); setMode('login'); resetState(); }}
            className="px-8 py-3 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap"
          >
            去登录
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf9f7] flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/e39d29f830118feadb3a8fe8cd7b74d0.jpg"
          alt="金榜旺财"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-12 left-10 right-10">
          <Link to="/" className="flex items-center gap-3 mb-8">
            <img src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg" alt="Logo" className="w-10 h-10 rounded-full object-cover" />
            <span className="text-white font-bold text-lg">金榜旺财</span>
          </Link>
          <h2 className="text-white text-3xl font-black mb-3">开启你的<br /><span className="text-amber-400">金榜之路</span></h2>
          <p className="text-white/70 text-sm leading-relaxed">加入50万+学生的学习社区，获取优质教学资源，结交志同道合的学习伙伴。</p>
          <div className="flex gap-6 mt-6">
            {[{ num: '50万+', label: '注册学生' }, { num: '8000+', label: '优秀教师' }, { num: '2万+', label: '精品课程' }].map(s => (
              <div key={s.label}>
                <div className="text-amber-400 font-black text-xl">{s.num}</div>
                <div className="text-white/60 text-xs">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Logo Mobile */}
          <Link to="/" className="flex items-center gap-2 mb-8 lg:hidden">
            <img src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg" alt="Logo" className="w-8 h-8 rounded-full object-cover" />
            <span className="font-bold text-red-700">金榜旺财</span>
          </Link>

          {/* Mode Tab */}
          <div className="flex bg-gray-100 rounded-full p-1 mb-6">
            {(['login', 'register'] as Mode[]).map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); resetState(); }}
                className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${mode === m ? 'bg-white text-gray-900' : 'text-gray-500'}`}
              >
                {m === 'login' ? '登录' : '注册'}
              </button>
            ))}
          </div>

          {/* Login Method Toggle */}
          {(mode === 'login' || (mode === 'register' && step === 1)) && step !== 'otp' && (
            <div className="flex bg-gray-50 border border-gray-200 rounded-xl p-1 mb-6 gap-1">
              <button
                onClick={() => { setLoginMethod('email'); setError(''); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${loginMethod === 'email' ? 'bg-white text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <i className="ri-mail-line text-sm"></i> 邮箱
              </button>
              <button
                onClick={() => { setLoginMethod('phone'); setError(''); }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${loginMethod === 'phone' ? 'bg-white text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <i className="ri-smartphone-line text-sm"></i> 手机号
              </button>
            </div>
          )}

          {/* ═══ LOGIN FORMS ═══ */}
          {mode === 'login' && (
            <>
              {/* Email Login */}
              {loginMethod === 'email' && (
                <form onSubmit={handleEmailLogin} className="flex flex-col gap-4">
                  <div>
                    <h1 className="text-2xl font-black text-gray-900 mb-1">欢迎回来</h1>
                    <p className="text-gray-500 text-sm">使用邮箱和密码登录</p>
                  </div>
                  <div className="flex flex-col gap-3 mt-1">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">邮箱</label>
                      <div className="flex items-center bg-gray-50 rounded-xl px-4 py-3 gap-2 border border-gray-200 focus-within:border-red-400 transition-colors">
                        <i className="ri-mail-line text-gray-400 text-sm"></i>
                        <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="请输入邮箱" className="flex-1 bg-transparent text-sm outline-none" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">密码</label>
                      <div className="flex items-center bg-gray-50 rounded-xl px-4 py-3 gap-2 border border-gray-200 focus-within:border-red-400 transition-colors">
                        <i className="ri-lock-line text-gray-400 text-sm"></i>
                        <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="请输入密码" className="flex-1 bg-transparent text-sm outline-none" />
                      </div>
                    </div>
                  </div>
                  {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
                  <button type="submit" disabled={loading} className="w-full py-3.5 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-60">
                    {loading ? '登录中...' : '立即登录'}
                  </button>
                </form>
              )}

              {/* Phone Login - send OTP */}
              {loginMethod === 'phone' && step === 1 && (
                <form onSubmit={handleSendOtp} className="flex flex-col gap-4">
                  <div>
                    <h1 className="text-2xl font-black text-gray-900 mb-1">手机号登录</h1>
                    <p className="text-gray-500 text-sm">输入手机号，接收验证码登录</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">手机号</label>
                    <div className="flex items-center bg-gray-50 rounded-xl px-4 py-3 gap-2 border border-gray-200 focus-within:border-red-400 transition-colors">
                      <span className="text-gray-500 text-sm font-medium whitespace-nowrap">+86</span>
                      <div className="w-px h-4 bg-gray-300"></div>
                      <i className="ri-smartphone-line text-gray-400 text-sm"></i>
                      <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="请输入手机号" className="flex-1 bg-transparent text-sm outline-none" />
                    </div>
                  </div>
                  {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
                  <button type="submit" disabled={loading} className="w-full py-3.5 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-60">
                    {loading ? '发送中...' : '获取验证码'}
                  </button>
                </form>
              )}

              {/* Phone Login - verify OTP */}
              {loginMethod === 'phone' && step === 'otp' && (
                <form onSubmit={handleVerifyOtp} className="flex flex-col gap-5">
                  <div>
                    <button type="button" onClick={() => { setStep(1); setOtp(''); setError(''); }} className="flex items-center gap-1 text-gray-400 text-sm mb-3 cursor-pointer hover:text-gray-600">
                      <i className="ri-arrow-left-line"></i> 重新输入手机号
                    </button>
                    <h1 className="text-2xl font-black text-gray-900 mb-1">输入验证码</h1>
                    <p className="text-gray-500 text-sm">验证码已发送至 <span className="font-semibold text-gray-700">+86 {form.phone}</span></p>
                  </div>
                  <OtpInput value={otp} onChange={setOtp} />
                  <div className="text-center">
                    {countdown > 0 ? (
                      <p className="text-gray-400 text-sm">{countdown}秒后可重新发送</p>
                    ) : (
                      <button type="button" onClick={handleResendOtp} className="text-red-600 text-sm font-medium cursor-pointer hover:underline">
                        重新发送验证码
                      </button>
                    )}
                  </div>
                  {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg text-center">{error}</p>}
                  <button type="submit" disabled={loading || otp.length < 6} className="w-full py-3.5 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-60">
                    {loading ? '验证中...' : '确认登录'}
                  </button>
                </form>
              )}
            </>
          )}

          {/* ═══ REGISTER FORMS ═══ */}
          {mode === 'register' && (
            <>
              {/* Step 1: Basic Info */}
              {step === 1 && (
                <form onSubmit={handleRegisterStep1} className="flex flex-col gap-4">
                  <div>
                    <h1 className="text-2xl font-black text-gray-900 mb-1">创建账号</h1>
                    <p className="text-gray-500 text-sm">第1步：选择身份，填写基本信息</p>
                  </div>

                  {/* Role Selection */}
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-2 block">我的身份</label>
                    <div className="grid grid-cols-2 gap-2">
                      {roleOptions.map(r => (
                        <button key={r.value} type="button" onClick={() => setRole(r.value)}
                          className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer text-left ${role === r.value ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                          <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${role === r.value ? 'bg-red-100' : 'bg-gray-100'}`}>
                            <i className={`${r.icon} text-sm ${role === r.value ? 'text-red-600' : 'text-gray-500'}`}></i>
                          </div>
                          <div>
                            <p className={`text-xs font-semibold ${role === r.value ? 'text-red-700' : 'text-gray-700'}`}>{r.label}</p>
                            <p className="text-xs text-gray-400 leading-tight">{r.desc}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Nickname */}
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">昵称 *</label>
                    <div className="flex items-center bg-gray-50 rounded-xl px-4 py-3 gap-2 border border-gray-200 focus-within:border-red-400 transition-colors">
                      <i className="ri-user-line text-gray-400 text-sm"></i>
                      <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="请输入昵称" className="flex-1 bg-transparent text-sm outline-none" />
                    </div>
                  </div>

                  {/* Email fields */}
                  {loginMethod === 'email' && (
                    <>
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">邮箱 *</label>
                        <div className="flex items-center bg-gray-50 rounded-xl px-4 py-3 gap-2 border border-gray-200 focus-within:border-red-400 transition-colors">
                          <i className="ri-mail-line text-gray-400 text-sm"></i>
                          <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="请输入邮箱" className="flex-1 bg-transparent text-sm outline-none" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">密码 *（至少6位）</label>
                        <div className="flex items-center bg-gray-50 rounded-xl px-4 py-3 gap-2 border border-gray-200 focus-within:border-red-400 transition-colors">
                          <i className="ri-lock-line text-gray-400 text-sm"></i>
                          <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="至少6位密码" className="flex-1 bg-transparent text-sm outline-none" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">确认密码 *</label>
                        <div className="flex items-center bg-gray-50 rounded-xl px-4 py-3 gap-2 border border-gray-200 focus-within:border-red-400 transition-colors">
                          <i className="ri-lock-2-line text-gray-400 text-sm"></i>
                          <input type="password" value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })} placeholder="再次输入密码" className="flex-1 bg-transparent text-sm outline-none" />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Phone field */}
                  {loginMethod === 'phone' && (
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">手机号 *</label>
                      <div className="flex items-center bg-gray-50 rounded-xl px-4 py-3 gap-2 border border-gray-200 focus-within:border-red-400 transition-colors">
                        <span className="text-gray-500 text-sm font-medium whitespace-nowrap">+86</span>
                        <div className="w-px h-4 bg-gray-300"></div>
                        <i className="ri-smartphone-line text-gray-400 text-sm"></i>
                        <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="请输入手机号" className="flex-1 bg-transparent text-sm outline-none" />
                      </div>
                    </div>
                  )}

                  {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
                  <button type="submit" className="w-full py-3.5 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap">
                    下一步
                  </button>
                </form>
              )}

              {/* Step 2: Profile Details (email register) */}
              {step === 2 && loginMethod === 'email' && (
                <form onSubmit={handleEmailRegisterSubmit} className="flex flex-col gap-4">
                  <div>
                    <button type="button" onClick={() => setStep(1)} className="flex items-center gap-1 text-gray-400 text-sm mb-3 cursor-pointer hover:text-gray-600">
                      <i className="ri-arrow-left-line"></i> 返回上一步
                    </button>
                    <h1 className="text-2xl font-black text-gray-900 mb-1">完善资料</h1>
                    <p className="text-gray-500 text-sm">第2步：填写详细信息，帮助匹配学伴</p>
                  </div>
                  <div className="flex flex-col gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">手机号（选填）</label>
                      <div className="flex items-center bg-gray-50 rounded-xl px-4 py-3 gap-2 border border-gray-200 focus-within:border-red-400 transition-colors">
                        <i className="ri-phone-line text-gray-400 text-sm"></i>
                        <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="请输入手机号" className="flex-1 bg-transparent text-sm outline-none" />
                      </div>
                    </div>
                    {(role === 'student' || role === 'parent') && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">{role === 'student' ? '当前年级' : '孩子年级'}（选填）</label>
                        <select value={form.grade} onChange={e => setForm({ ...form, grade: e.target.value })} className="w-full bg-gray-50 rounded-xl px-4 py-3 text-sm outline-none cursor-pointer border border-gray-200">
                          <option value="">请选择年级</option>
                          {gradeOptions.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                      </div>
                    )}
                    {role === 'teacher' && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">教授科目（选填）</label>
                        <div className="flex items-center bg-gray-50 rounded-xl px-4 py-3 gap-2 border border-gray-200 focus-within:border-red-400 transition-colors">
                          <i className="ri-book-open-line text-gray-400 text-sm"></i>
                          <input type="text" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="如：高中数学、考研英语" className="flex-1 bg-transparent text-sm outline-none" />
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">所在地区（选填）</label>
                      <div className="flex items-center bg-gray-50 rounded-xl px-4 py-3 gap-2 border border-gray-200 focus-within:border-red-400 transition-colors">
                        <i className="ri-map-pin-line text-gray-400 text-sm"></i>
                        <input type="text" value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} placeholder="如：北京市海淀区" className="flex-1 bg-transparent text-sm outline-none" />
                      </div>
                    </div>
                  </div>
                  {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
                  <button type="submit" disabled={loading} className="w-full py-3.5 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-60">
                    {loading ? '注册中...' : '完成注册'}
                  </button>
                </form>
              )}

              {/* Step 2: Profile Details (phone register) - send OTP */}
              {step === 2 && loginMethod === 'phone' && (
                <form onSubmit={handlePhoneRegisterSendOtp} className="flex flex-col gap-4">
                  <div>
                    <button type="button" onClick={() => setStep(1)} className="flex items-center gap-1 text-gray-400 text-sm mb-3 cursor-pointer hover:text-gray-600">
                      <i className="ri-arrow-left-line"></i> 返回上一步
                    </button>
                    <h1 className="text-2xl font-black text-gray-900 mb-1">完善资料</h1>
                    <p className="text-gray-500 text-sm">第2步：填写详细信息，然后验证手机号</p>
                  </div>
                  <div className="flex flex-col gap-3">
                    {(role === 'student' || role === 'parent') && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">{role === 'student' ? '当前年级' : '孩子年级'}（选填）</label>
                        <select value={form.grade} onChange={e => setForm({ ...form, grade: e.target.value })} className="w-full bg-gray-50 rounded-xl px-4 py-3 text-sm outline-none cursor-pointer border border-gray-200">
                          <option value="">请选择年级</option>
                          {gradeOptions.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                      </div>
                    )}
                    {role === 'teacher' && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">教授科目（选填）</label>
                        <div className="flex items-center bg-gray-50 rounded-xl px-4 py-3 gap-2 border border-gray-200 focus-within:border-red-400 transition-colors">
                          <i className="ri-book-open-line text-gray-400 text-sm"></i>
                          <input type="text" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="如：高中数学、考研英语" className="flex-1 bg-transparent text-sm outline-none" />
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">所在地区（选填）</label>
                      <div className="flex items-center bg-gray-50 rounded-xl px-4 py-3 gap-2 border border-gray-200 focus-within:border-red-400 transition-colors">
                        <i className="ri-map-pin-line text-gray-400 text-sm"></i>
                        <input type="text" value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} placeholder="如：北京市海淀区" className="flex-1 bg-transparent text-sm outline-none" />
                      </div>
                    </div>
                  </div>
                  {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
                  <button type="submit" disabled={loading} className="w-full py-3.5 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-60">
                    {loading ? '发送中...' : '发送验证码'}
                  </button>
                </form>
              )}

              {/* OTP Verification (phone register) */}
              {step === 'otp' && loginMethod === 'phone' && (
                <form onSubmit={handlePhoneRegisterVerify} className="flex flex-col gap-5">
                  <div>
                    <button type="button" onClick={() => { setStep(2); setOtp(''); setError(''); }} className="flex items-center gap-1 text-gray-400 text-sm mb-3 cursor-pointer hover:text-gray-600">
                      <i className="ri-arrow-left-line"></i> 返回
                    </button>
                    <h1 className="text-2xl font-black text-gray-900 mb-1">验证手机号</h1>
                    <p className="text-gray-500 text-sm">验证码已发送至 <span className="font-semibold text-gray-700">+86 {form.phone}</span></p>
                  </div>
                  <OtpInput value={otp} onChange={setOtp} />
                  <div className="text-center">
                    {countdown > 0 ? (
                      <p className="text-gray-400 text-sm">{countdown}秒后可重新发送</p>
                    ) : (
                      <button type="button" onClick={handleResendOtp} className="text-red-600 text-sm font-medium cursor-pointer hover:underline">
                        重新发送验证码
                      </button>
                    )}
                  </div>
                  {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg text-center">{error}</p>}
                  <button type="submit" disabled={loading || otp.length < 6} className="w-full py-3.5 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-60">
                    {loading ? '验证中...' : '完成注册'}
                  </button>
                </form>
              )}
            </>
          )}

          <p className="text-center text-xs text-gray-400 mt-6">
            注册即代表同意
            <span className="text-red-600 cursor-pointer hover:underline mx-1">用户协议</span>
            和
            <span className="text-red-600 cursor-pointer hover:underline mx-1">隐私政策</span>
          </p>
        </div>
      </div>
    </div>
  );
}
